import { CommunityPostOverviewItemModel } from "client/Api/Hub/hub";
import { CommunityPostImageViewModel } from "client/Api/Communities/communityPostViewer";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	isNotNil,
	isNotNilOrWhiteSpace,
	setTrackingId
} from "client/Misc/utils";
import { ReactElement, useMemo } from "react";
import { FormattedText } from "client/Components";
import { getCommunityPostImageUrl } from "../Communities/Composer/common";
import styles from "./CommunityPostCardItem.module.less";
import { localize } from "client_legacy/ndaw/culture";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { formatUnit } from "client/Misc/cultureSpecificUtils";
import { useHubStatisticsFormatter } from "../useHubStatisticsFormatter";
import { NumberFormatter } from "client/Hooks";
import { ReactionsCountButton } from "client/Features/Reactions/ReactionsCountButton/ReactionsCountButton";
import { PostHeader } from "../Communities/PostHeader";
import { htmlToPlainText } from "client/Misc/htmlUtils";

export type CommunityPostCardItemProps = {
	model: CommunityPostOverviewItemModel;
	onClick: () => void;
	onNavigateToCommunity: () => void;
	timezone: TimeZone;
	trackingId: string;
};

export const CommunityPostCardItem = (props: CommunityPostCardItemProps): ReactElement => {
	const {
		model,
		onClick,
		onNavigateToCommunity,
		timezone,
		trackingId
	} = props;

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();

	const authorDisplayName = useMemo(
		(): string => {
			if (!model.createdBy) {
				return localize("_DeletedUser");
			}

			return getComputedDisplayName(
				model.createdBy
			)
				.computedDisplayName;
		},
		[model.createdBy]
	);

	const plainMessage = useMemo(
		(): string | null => {
			return isNotNil(model.message)
				? htmlToPlainText(model.message)
				: null;
		},
		[model.message]
	);

	const firstImage: CommunityPostImageViewModel | undefined = model.images[0];

	const renderMessage = (): ReactElement | null => {
		if (isNotNilOrWhiteSpace(plainMessage)) {
			return (
				<>
					{plainMessage}
				</>
			);
		}

		if (isNotNil(model.pollQuestion)) {
			return (
				<>
					<span
						{...setTrackingId("info", "|has-poll")}
						className={styles.info}
					>
						<FormattedText
							text={localize("_UserNamePostedAPoll")}
							thingsToReplace={[
								{
									what: "{{UserName}}",
									filler: authorDisplayName
								}
							]}
						/>
					</span>
					<div>
						{model.pollQuestion}
					</div>
				</>
			);
		}

		if (isNotNil(firstImage)) {
			return (
				<span
					{...setTrackingId("info", "|has-image")}
					className={styles.info}
				>
					<FormattedText
						text={localize("_UserNamePostedAnImage")}
						thingsToReplace={[
							{
								what: "{{UserName}}",
								filler: authorDisplayName
							}
						]}
					/>
				</span>
			);
		}

		if (model.hasLinkedDocuments) {
			return (
				<span
					{...setTrackingId("info", "|has-linked-documents")}
					className={styles.info}
				>
					<FormattedText
						text={localize("_UserNamePostedAttachments")}
						thingsToReplace={[
							{
								what: "{{UserName}}",
								filler: authorDisplayName
							}
						]}
					/>
				</span>
			);
		}

		return null;
	};

	return (
		<div
			{...setTrackingId(trackingId)}
			onClick={() => onClick()}
			className={styles.root}
			tabIndex={0}
		>
			<div className={styles.header}>
				<PostHeader
					trackingId="header"
					authorUserInfo={model.createdBy}
					community={model.community}
					publicationDate={model.createdOn}
					timezone={timezone}
					isInsideClickableArea
					onNavigateToCommunity={onNavigateToCommunity}
				/>
			</div>
			<div
				{...setTrackingId("content")}
				className={styles.content}
			>
				<div className={styles.message}>
					{renderMessage()}
				</div>
			</div>
			{isNotNil(firstImage) && (
				<img
					{...setTrackingId("image")}
					src={getCommunityPostImageUrl(firstImage.blobId)}
					className={styles.image}
				/>
			)}
			<div
				{...setTrackingId("statistics")}
				className={styles.statistics}
			>
				<div>
					<ReactionsCountButton
						trackingId="reactions-count"
						counters={model.statistics.reactionCounters}
					/>
				</div>
				<div
					{...setTrackingId("comment-counter")}
				>
					{formatUnit({
						value: model.statistics.commentCount,
						pluralForm: "_NComments",
						singularForm: "_1Comment",
						valueFormatter: statisticsFormatter
					})}
				</div>
			</div>
		</div>
	);
};
CommunityPostCardItem.displayName = "CommunityPostCardItem";
